import formattingUtilities from '@/utilities/formattingUtilities'

export default class ApiUtility {
  constructor() {
    this.defaultParams = {
      page: 1,
      limit: DEFAULT_PER_PAGE_LIMIT,
      sort_by: undefined,
      sort_dir: undefined,
      with: undefined,
      appends: undefined,
      select: undefined,
      distinct: undefined,
      make_hidden: undefined,
      filters: {},
      resource: undefined
    }
    this.defaultFilters = {}
    this.defaultSort = {
      sort_dir: undefined,
      sort_by: undefined,
      sort_scope: undefined,
      sort_appends: undefined,
      sort_skus: undefined,
      sort_comparison_method: undefined,
    }

    this.params = { ...this.defaultParams }
    this.service = null
  }

  /**
   * @param {Object} apiService
   * @param {Object} defaultFilters
   */
  init(apiService, defaultFilters) {
    this.initService(apiService)
    this.initFilters(defaultFilters)
  }

  /**
   * @param {Object} apiService
   */
  initService(apiService) {
    this.service = apiService
  }

  /**
   * @param {Object} values
   */
  initFilters(values) {
    this.defaultFilters = { ...values }
    this.params.filters = { ...values }
  }

  /**
   * @param {Object} values
   */
  setDefaultFilters(values) {
    this.defaultFilters = { ...values }
  }

  /**
   * @returns {Object}
   */
  getDefaultFilters() {
    return this.defaultFilters
  }

  /**
   * @param {Object} values
   */
  setFilters(values) {
    this.params.filters = { ...values }
    this.params.page = 1
  }

  /**
   * @param {number} value
   */
  setPageLimit(value) {
    this.params.limit = value
  }

  setGetAllEntries() {
    this.params.limit = 0
  }

  /**
   * @param {string} value
   */
  setWith(value) {
    this.params.with = value
  }

  /**
   * @param {string} value
   */
  setAppends(value) {
    this.params.appends = value
  }

  /**
   * @param {string} value
   */
  setSelect(value) {
    this.params.select = value
  }

  /**
   * @param {boolean} value
   */
  setDistinct(value) {
    this.params.distinct = value === true ? 1 : 0
  }

  /**
   * @param {string} value
   */
  setMakeHidden(value) {
    this.params.make_hidden = value
  }

  /**
   * @param {string} sortBy
   * @param {string|undefined} sortDir
   * @param {string|undefined} sortScope
   */
  setDefaultSort(sortBy, sortDir = 'asc', sortScope = undefined) {
    this.params.sort_by = sortBy
    this.defaultSort.sort_by = sortBy
    this.params.sort_dir = sortDir
    this.defaultSort.sort_dir = sortDir
    this.params.sort_scope = sortScope
    this.defaultSort.sort_scope = sortScope
  }

  /**
   * @param {Object} values
   */
  setParams(values) {
    Object.keys(values).forEach((v) => (this.params[v] = values[v]))
  }

  resetFilters() {
    this.params.filters = { ...this.defaultFilters }
  }

  getCurrentFilters() {
    return this.params.filters
  }

  resetParams() {
    this.params = JSON.parse(JSON.stringify(this.defaultParams))
  }

  /**
   * @param {Object} event
   * @param {Object} loading
   * @param {Function} callback
   */
  onSort(event, loading, callback) {
    if (event.sortField) {
      this.params.sort_dir = event.sortOrder === 1 ? 'asc' : 'desc'
      this.setSortByParam(event.sortField)
    } else {
      // Return data to default order.
      this.params.sort_dir = this.defaultSort.sort_dir
      this.params.sort_by = this.defaultSort.sort_by
      this.params.sort_scope = this.defaultSort.sort_scope
      this.params.sort_appends = this.defaultSort.sort_appends
      this.params.sort_skus = this.defaultSort.sort_skus
      this.params.sort_comparison_method = this.defaultSort.sort_comparison_method
    }

    loading.value = true

    this.params.page = 1
    this.loadData(callback)
  }

  /**
   * @param {Function|string} sortField
   */
  setSortByParam(sortField) {
    let sort = {}
    if (typeof sortField === 'function') {
      sort = sortField()
    } else {
      sort.field = sortField
    }
    if (sort.field.indexOf('scope') === 0) {
      this.params.sort_scope = this.formatScopeSortField(sort.field)
      this.params.sort_by = undefined
      this.params.sort_appends = undefined
      this.params.sort_skus = undefined
      this.params.sort_comparison_method = undefined
    } else if (sort.field.indexOf('appends_') === 0) {
      this.params.sort_appends = this.formatAppendSortField(sort.field)
      this.params.sort_skus = undefined
      this.params.sort_by = undefined
      this.params.sort_scope = undefined
      this.params.sort_comparison_method = sort.comparison
    } else {
      this.params.sort_by = sort.field
      this.params.sort_scope = undefined
      this.params.sort_appends = undefined
      this.params.sort_skus = undefined
      this.params.sort_comparison_method = undefined
    }
  }

  /**
   * @param {string} field
   */
  formatScopeSortField(field) {
    const scope = field.replace('scope', '')
    return scope.charAt(0).toLowerCase() + scope.slice(1)
  }

  /**
   * @param {string} field
   */
  formatAppendSortField(field) {
    return field.replace('appends_', '')
  }

  /**
   * @param {string} filter
   * @param {*} value
   * @param {Object} loading
   * @param {Function} callback
   */
  onFilter(filter, value, loading, callback) {
    loading.value = true
    this.params.filters[filter] = value
    this.params.page = 1
    this.loadData(callback)
  }

  /**
   * @param {string|string[]} filter
   * @param {Object} loading
   * @param {Function} callback
   */
  clearFilter(filter, loading, callback) {
    loading.value = true
    if (Array.isArray(filter)) {
      for (let i = 0; i < filter.length; i++) {
        this.params.filters[filter[i]] = undefined
      }
    } else {
      this.params.filters[filter] = undefined
    }
    this.params.page = 1
    this.loadData(callback)
  }

  /**
   * @param {string} filter
   */
  removeFilter(filter) {
    this.params.filters[filter] = undefined
  }

  /**
   * @param {Object} loading
   * @param {Function} callback
   */
  clearFilters(loading, callback) {
    loading.value = true
    this.resetFilters()
    this.params.page = 1
    this.loadData(callback)
  }

  /**
   * @param {string} filter
   * @param {*} value
   */
  addFilterNoLoad(filter, value) {
    this.params.filters[filter] = value
  }

  /**
   * @param {string|string[]} filter
   */
  clearFilterNoLoad(filter) {
    if (Array.isArray(filter)) {
      for (let i = 0; i < filter.length; i++) {
        this.params.filters[filter[i]] = undefined
      }
    } else {
      this.params.filters[filter] = undefined
    }
  }

  /**
   * @param {Object} event
   * @param {Object} loading
   * @param {Function} callback
   */
  onPage(event, loading, callback) {
    loading.value = true
    this.params.page = event.page + 1
    this.params.limit = event.rows
    this.loadData(callback)
  }

  formatParams() {
    return { params: formattingUtilities.formatQueryParams(this.params) }
  }

  /**
   * @param {Function} callback
   */
  loadData(callback) {
    // If loadData includes additional parameters, they exist as properties within the arguments object, starting with param '1'.
    if (arguments.hasOwnProperty('1')) {
      // The 'arguments' var is an object with a 0-based list of all parameters included in the loadData function call.
      const loadDataArguments = arguments

      // Since the callback itself is the first element, remove it before sending the remaining list of arguments downstream.
      delete loadDataArguments[0]

      // Pass the remaining list of arguments into the callback!
      if (this.service) this.service.list(this.formatParams()).then(response => {
        callback(response, ...Object.values(loadDataArguments))
      })
    } else {
      // Otherwise, do the default call.
      if (this.service) this.service.list(this.formatParams()).then(callback)
    }
  }

  /**
   * @param {Function} callback
   * @param {string} reportFunction
   */
  generateReport(callback, reportFunction = 'report') {
    if (this.service) this.service[reportFunction](this.formatParams()).then(callback)
  }

  /**
   * @deprecated
   * @param {*} value
   */
  setReportCSVMap(value) {
    this.params.setCsvMap = value
  }

  /**
   * @deprecated use generateReport method instead
   * @param {Object} loading
   * @param {Function} callback
   */
  onFetchAll(loading, callback) {
    loading.value = true
    const originalLimit = this.params.limit
    const originalPage = this.params.page
    this.params.limit = 0
    this.params.page = 1
    this.loadData(callback)
    this.params.limit = originalLimit
    this.params.page = originalPage
  }

}
